import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Link2 } from "lucide-react";

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    name: "", business_name: "", pan: "", gstin: "",
    address: "", bank_account: "", bank_ifsc: "", bank_name: "",
    gst_state_category: "regular", age_group: "below_60",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", business_name: user.business_name || "",
      pan: user.pan || "", gstin: user.gstin || "", address: user.address || "",
      bank_account: user.bank_account || "", bank_ifsc: user.bank_ifsc || "",
      bank_name: user.bank_name || "",
      gst_state_category: user.gst_state_category || "regular",
      age_group: user.age_group || "below_60",
    });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/profile", form);
      updateUser(r.data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="tc-overline mb-2">Your studio</p>
        <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-[#5a5a53] mt-2">
          This info shows up on your invoice PDFs. Keep your GSTIN and PAN current.
        </p>
      </div>

      <div className="tc-card p-6 space-y-5">
        <Section title="Identity">
          <Field label="Display name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="settings-name" />
          <Field label="Business name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} testId="settings-business" />
        </Section>

        <Section title="Tax IDs">
          <Field label="PAN" value={form.pan} onChange={(v) => setForm({ ...form, pan: v.toUpperCase() })} testId="settings-pan" mono />
          <Field label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v.toUpperCase() })} testId="settings-gstin" mono />
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">State category</Label>
            <Select value={form.gst_state_category} onValueChange={(v) => setForm({ ...form, gst_state_category: v })}>
              <SelectTrigger data-testid="settings-state-category" className="mt-2 h-11 rounded-md border-[#e5e5e0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular · ₹20L GST threshold</SelectItem>
                <SelectItem value="special">Special category · ₹10L GST threshold</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#a3a39b] mt-1.5">
              Special = Manipur, Mizoram, Nagaland, Tripura.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Age group</Label>
            <Select value={form.age_group} onValueChange={(v) => setForm({ ...form, age_group: v })}>
              <SelectTrigger data-testid="settings-age-group" className="mt-2 h-11 rounded-md border-[#e5e5e0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="below_60">Below 60</SelectItem>
                <SelectItem value="60_to_80">Senior (60–80)</SelectItem>
                <SelectItem value="above_80">Super senior (80+)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#a3a39b] mt-1.5">
              Used for old regime income-tax slabs.
            </p>
          </div>
        </Section>

        <Section title="Address">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Business address</Label>
            <Textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-2 rounded-md border-[#e5e5e0]"
              rows={3}
              data-testid="settings-address"
            />
          </div>
        </Section>

        <Section title="Bank details (for invoice PDFs)">
          <Field label="Bank name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} testId="settings-bank-name" />
          <Field label="IFSC" value={form.bank_ifsc} onChange={(v) => setForm({ ...form, bank_ifsc: v.toUpperCase() })} testId="settings-bank-ifsc" mono />
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Account number</Label>
            <Input
              value={form.bank_account}
              onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
              className="mt-2 h-11 rounded-md border-[#e5e5e0] font-mono"
              data-testid="settings-bank-account"
            />
          </div>
        </Section>

        <div className="flex justify-end pt-4 border-t border-[#e5e5e0]">
          <Button data-testid="settings-save-btn" onClick={save} disabled={saving}
            className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </div>

      <PaymentGatewaysCard />
    </div>
  );
}

function PaymentGatewaysCard() {
  const [gateways, setGateways] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");

  const fetchGateways = async () => {
    try {
      const r = await api.get("/gateways");
      setGateways(r.data || []);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => { fetchGateways(); }, []);

  const connect = async () => {
    if (!keyId || !keySecret) { toast.error("Enter Key ID and Key Secret"); return; }
    setConnecting(true);
    try {
      await api.post("/gateways/connect", {
        provider: "razorpay",
        key_id: keyId.trim(),
        key_secret: keySecret.trim(),
        webhook_secret: webhookSecret.trim() || null,
      });
      toast.success("Razorpay connected");
      setKeyId(""); setKeySecret(""); setWebhookSecret("");
      fetchGateways();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't connect");
    } finally { setConnecting(false); }
  };

  const disconnect = async (provider) => {
    await api.delete(`/gateways/${provider}`);
    toast.success("Disconnected");
    fetchGateways();
  };

  const razorpay = gateways.find((g) => g.provider === "razorpay");
  const webhookUrl = `${window.location.origin.replace(/\/$/, "")}/api/webhooks/razorpay`;

  return (
    <div className="tc-card p-6 space-y-5" data-testid="gateways-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 bg-[#0a0a0a] text-[#fafaf8]">
          <CreditCard size={18} />
        </div>
        <div>
          <p className="tc-overline">Payments</p>
          <h2 className="font-['Outfit'] text-2xl font-medium tracking-tight mt-1">Payment gateways</h2>
          <p className="text-sm text-[#5a5a53] mt-2">
            Connect Razorpay once, then generate a <em>"Pay now"</em> link per invoice.
            Clients pay via UPI/card/netbanking. We auto-mark paid when the webhook fires.
          </p>
        </div>
      </div>

      {razorpay ? (
        <div className="rounded-md border border-[#eef3ee] bg-[#f5f9f5] p-4 flex items-start justify-between gap-4" data-testid="razorpay-connected">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={18} className="text-[#4a6b53] mt-0.5" />
            <div>
              <div className="font-bold text-sm text-[#0a0a0a]">Razorpay connected</div>
              <div className="text-xs text-[#5a5a53] font-mono mt-1">{razorpay.key_id_masked}</div>
              <div className="text-[11px] text-[#a3a39b] mt-1">
                Webhook: {razorpay.webhook_configured ? "configured" : "not set — payment status may not auto-update"}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => disconnect("razorpay")}
            data-testid="razorpay-disconnect-btn"
            className="text-[#b83a26] hover:bg-[#ffece8] text-xs uppercase tracking-wider font-bold"
          >
            Disconnect
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="razorpay-connect-form">
          <p className="tc-overline">Connect Razorpay</p>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Key ID</Label>
              <Input
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
                placeholder="rzp_test_XXXXXXXXXXXX"
                className="mt-2 h-11 rounded-md border-[#e5e5e0] font-mono"
                data-testid="razorpay-key-id-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Key secret</Label>
              <Input
                type="password"
                value={keySecret}
                onChange={(e) => setKeySecret(e.target.value)}
                placeholder="Stored encrypted"
                className="mt-2 h-11 rounded-md border-[#e5e5e0] font-mono"
                data-testid="razorpay-key-secret-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Webhook secret (optional)</Label>
              <Input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="Only needed if you set up webhook auto-sync"
                className="mt-2 h-11 rounded-md border-[#e5e5e0] font-mono"
                data-testid="razorpay-webhook-secret-input"
              />
            </div>
          </div>
          <Button
            onClick={connect}
            disabled={connecting}
            data-testid="razorpay-connect-btn"
            className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md"
          >
            {connecting ? "Validating..." : "Connect Razorpay"}
          </Button>
          <p className="text-[11px] text-[#a3a39b]">
            Find keys at Razorpay Dashboard → Account & Settings → API Keys.
          </p>
        </div>
      )}

      <div className="border-t border-[#e5e5e0] pt-4">
        <p className="tc-overline mb-2">Webhook URL</p>
        <div className="flex items-center gap-2 bg-[#fafaf8] border border-[#e5e5e0] rounded-md px-3 py-2 text-xs font-mono text-[#0a0a0a]">
          <Link2 size={14} className="text-[#5a5a53]" />
          <span className="flex-1 truncate" data-testid="razorpay-webhook-url">{webhookUrl}</span>
          <button
            className="text-[10px] uppercase tracking-wider font-bold text-[#d95d39] hover:underline"
            onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }}
            data-testid="razorpay-webhook-copy-btn"
          >
            Copy
          </button>
        </div>
        <p className="text-[11px] text-[#a3a39b] mt-2">
          In Razorpay dashboard → Webhooks → Add Webhook. Select events: <span className="font-mono">payment_link.paid</span>, <span className="font-mono">payment_link.partially_paid</span>, <span className="font-mono">payment_link.expired</span>, <span className="font-mono">payment.captured</span>.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="tc-overline mb-3">{title}</p>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, testId, mono }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className={`mt-2 h-11 rounded-md border-[#e5e5e0] ${mono ? "font-mono" : ""}`}
        data-testid={testId} />
    </div>
  );
}
